import type { CShopTranslations } from "./pl";

export const en: CShopTranslations = {
  cart: {
    title: "Cart",
    empty: "Your cart is empty, please add products to place an order.",
    productId: "Product ID",
    productCategory: "Category",
    productVariant: "Variant",
    productCount: "Quantity",
    productPrice: "Price (gross)",
    productPriceBeforePromotion: "Price before promotion (gross)",
    productSum: "Line total (gross)",
    variantColorAlt: "Color swatch",
    summary: "Summary",
    pricesIncludeVat: "All amounts are gross (VAT included).",
    sumCount: "Items",
    sumProducts: "Products",
    sumDelivery: "Shipping",
    discount: "Discount",
    total: "Total (gross)",
    continueShopping: "Continue shopping",
    gotoOrder: "Proceed to checkout",
    countLabel: "Quantity",
    countDecrement: "Decrease quantity",
    countIncrement: "Increase quantity",
    deleteItem: "Remove item from cart",
    updateFailed: "Failed to update quantity. Please try again.",
    deleteFailed: "Failed to remove item. Please try again.",
    gotoOrderDisabledNoPayment: "Select a payment option above to proceed to checkout.",
    gotoOrderDisabledNoDelivery: "Select a delivery option above to proceed to checkout.",
    gotoOrderDisabledStock: "One of the items exceeds available stock - adjust the quantity to continue.",
  },
  payment: {
    heading: "Payment options",
    emptyList:
      "No payment options are available — please contact the shop administrator.",
    changeFailed: "Failed to change the payment option. Please try again.",
    disabledOverMax:
      "Online payment is not available for orders over {amount} {currency}. Please pick a bank transfer.",
    gotoOrderDisabledOverMax:
      "The selected online payment is not available for this amount - pick another option.",
  },
  delivery: {
    heading: "Delivery options",
    pickPaymentFirst: "Please select a payment option first.",
    emptyList:
      "No delivery options for the selected payment — please contact the shop administrator.",
    changeFailed: "Failed to change the delivery option. Please try again.",
    free: "free",
  },
  discount: {
    heading: "Discount code",
    placeholder: "Enter a discount code",
    apply: "Apply",
    remove: "Remove code",
    hint: "Discount applies to products not already on promotion.",
    active: "Code applied",
    statusEmpty: "Enter a discount code.",
    statusOk: "Code is valid.",
    statusNotExists: "This code does not exist.",
    statusExpired: "This code has expired.",
    statusUsed: "This code has already been used.",
    statusExhausted: "This code has reached its usage limit.",
    checkFailed: "Failed to verify the code. Please try again.",
  },
  freeDelivery: {
    threshold: "Order for {price} {currency} or more to get free shipping.",
    is: "You qualify for free shipping.",
    remaining: "{remaining} {currency} to go for free shipping.",
  },
  order: {
    title: "Checkout",
    sectionContact: "Contact details",
    sectionSendAddress: "Shipping address",
    sectionFvatData: "Invoice details",
    sectionCheckboxData: "Consents",
    labelEmail: "Email",
    labelName: "First name",
    labelSurname: "Last name",
    labelTelephone: "Phone",
    labelCity: "City",
    labelPostCode: "Postcode",
    labelStreet: "Street",
    labelNr: "No.",
    labelPass: "Password",
    labelPPass: "Repeat password",
    labelCompanyName: "Company name or full name",
    labelNip: "Tax ID",
    labelCountry: "Country",
    labelBillingCountry: "Country (invoice)",
    countrySelectPlaceholder: "Select country",
    fvatCheckbox: "I want a VAT invoice",
    regulaminCheckbox:
      "I have read and accept the [terms and conditions]({regulamin_address}) of {host}.",
    personalDataCheckbox:
      "I consent to processing of my personal data.",
    marketingCheckbox:
      "I consent to receiving electronic marketing communications from {host}.",
    tabNewClient: "New customer",
    tabJustOrder: "Order without registration",
    tabLogin: "Log in and order",
    loginPromptTitle: "Log in",
    loginPromptHint:
      "Log in to place an order with your saved details. You will return here after signing in.",
    loginLabelLogin: "Email or username",
    loginLabelPassword: "Password",
    loginSubmit: "Log in",
    loginSubmitBusy: "Signing in…",
    loginFailed: "Sign-in failed. Check your details and try again.",
    empty: "Your cart is empty. Return to the shop to add products.",
    backToCard: "Back to cart",
    submit: "Place order",
    submitBusy: "Sending…",
    topError: "Failed to place the order. Please try again.",
    validationRequired: 'Field "{field}" is required.',
    validationEmail: "Invalid email address.",
    validationTelephone: "Invalid phone number.",
    validationPassMinLength: "Password must be at least 8 characters.",
    validationPassMismatch: "Repeated password does not match.",
    validationRegulamin: "You must accept the terms and conditions.",
    validationPersonalData: "You must consent to processing of personal data.",
    summaryHeading: "Your order",
  },
  inpost: {
    heading: "InPost parcel locker",
    noneSelected: "No parcel locker selected yet.",
    gotoOrderRequired: "Please choose an InPost locker before proceeding to checkout.",
    change: "Change locker",
    remove: "Clear selection",
    select: "Choose parcel locker",
    modalTitle: "Choose InPost parcel locker",
    modalClose: "Close",
    searchPlaceholder: "Enter locker number, city, street or postal code",
    searchAriaLabel: "Search for a parcel locker",
    searchHint: "Type a phrase above to search, or click a marker on the map.",
    searchLoading: "Searching…",
    searchNoResults: "No results for the entered phrase.",
    nearest: "Find nearest locker",
    nearestBusy: "Getting your location…",
    geolocationDenied: "Location access was denied. Please search by city instead.",
    geolocationUnsupported: "Your browser does not support geolocation.",
    nearestNotFound: "No locker was found near the given location.",
    loadFailed: "Failed to load parcel locker data. Please try again.",
    saveFailed: "Failed to save the selected parcel locker. Please try again.",
    mapFailed: "Failed to load the map. Please use the search field below.",
    mapUnavailable: "Map unavailable - please use the search field below.",
    confirm: "Confirm selection",
    cancel: "Cancel",
    listResultsAriaLabel: "Parcel locker search results",
    selectedHeading: "Selected parcel locker",
    selectedClear: "Clear selection",
  },
  pickupPoint: {
    heading: "Pickup point",
    noneSelected: "No pickup point selected yet.",
    gotoOrderRequired: "Please choose a pickup point before proceeding to checkout.",
    change: "Change point",
    remove: "Clear selection",
    select: "Choose pickup point",
    modalTitle: "Choose pickup point",
    modalClose: "Close",
    searchPlaceholder: "Enter city, name or postal code",
    searchAriaLabel: "Filter pickup points",
    emptyList: "No pickup points available for this delivery option.",
    noSearchMatch: "No point matches the search.",
    loading: "Loading…",
    loadFailed: "Failed to load the pickup point list. Please try again.",
    saveFailed: "Failed to save the selected pickup point. Please try again.",
    confirm: "Confirm selection",
    cancel: "Cancel",
    navigate: "Navigate in Google Maps",
    openHoursHeading: "Opening hours",
    openHoursMonFri: "Mon-Fri",
    openHoursSat: "Saturday",
    openHoursSun: "Sunday",
    openHoursClosed: "Closed",
    openHoursAllDay: "24 hours",
    listResultsAriaLabel: "List of available pickup points",
    selectedClear: "Clear selection",
  },
  orderComplete: {
    heading: "Your order {nr} has been received. Thank you!",
    toPaySum: "Order total (gross): {amount}",
    orderNr: "Your order number is {nr}",
    paidBanner: "The order has been paid.",
    yourOrders: "Your orders",
    goToPayment: "Pay now online",
    notFound: "Order not found. Check the link from the email or sign in and go to your orders list.",
    paymentInfoTransfer: "Payment can be made to the bank account number:",
    paymentInfoOnline: "You can finish payment using our web payment system",
    paymentInfoOnlineWithAccount: "You can finish payment using our web payment system or by transfer to bank account:",
    paymentInfoCash: "Payment can be made in cash at points of sale:",
  },
  formatPrice: (value: number, currency: string): string => {
    const rounded = (Math.round(value * 100) / 100).toFixed(2);
    return currency ? `${rounded} ${currency}` : rounded;
  },
};
